/**
 * @file billLineSplit.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-20
 * @description One bill line split over several PO lines (Cam, 14 Oct 2026:
 *              "one bill to two Xero source POs where the bill has 10T on in
 *              one line and the POs each have 1T and 9T respectively of the
 *              same product … the user [must] be able to essentially split
 *              the bill line over the two POs' lines"). Pure — Add / Edit Bill
 *              and Bulk Add Bills keep the lines; this finds the case, works
 *              out the quantities and money, and turns one line into several
 *              ORDINARY bill lines (each on its PO and PO line). Nothing new
 *              reaches the server: it sees bill lines, as always.
 *
 * THE CASE (detection — findSplit). A bill line whose quantity equals the SUM
 * of two or more PO lines of the same product, where no single one of them
 * matches it on its own:
 *   - same product: the item code (case and spaces ignored); a line with no
 *     code, the same words. Different products are never combined, and lines
 *     counted in different units (one PO in bags, the other in tonnes) are
 *     never added together.
 *   - each PO line counts what was SUPPLIED where recorded, else what was
 *     ordered (utils/billQtyCheck.compareQuantity, a Xero-made PO ordered).
 *   - "equals" is billQtyCheck's own: within 0.5 T (bulk) or 1 bag / unit,
 *     in the PO line's unit — a bill per tonne against POs in bags is
 *     converted first (utils/billUnitConversion).
 *   - several sets of PO lines fitting equally well is ambiguous: offered,
 *     the user chooses.
 * The quantities: each PO line takes what it counts; any difference between
 * the bill and their sum goes on the largest. In the bill's unit when it
 * differs from the PO's (1.00 T against 10 + 30 bags → 0.25 T + 0.75 T).
 *
 * THE MONEY (splitAmounts). The supplier's line must still come to exactly
 * what it did — the bill's net, VAT and total do not move, and still match
 * the invoice:
 *   - every line keeps the line's unit price; each line's amount is its
 *     quantity × that price, to the penny, as Xero works it out (both at 4 dp).
 *   - when those do not add up to the original line's amount (a quantity with
 *     a fraction of a penny in it), the LARGEST line takes the remainder: its
 *     price moves at the 4th decimal place just enough (278.67 → 278.6711) —
 *     Xero insists a line's amount is its quantity × price, so the amount
 *     cannot simply be written in.
 *   - VAT likewise: each line's VAT is its own amount's; a penny lost or
 *     gained in rounding goes on the largest line as its VAT (`_vat` on the
 *     grid line — OrderLinesGrid.lineTotals honours it while that line's
 *     amount and tax type are unchanged; it is saved as the line's tax_amount
 *     and sent to Xero as its TaxAmount). Only when every line has the same
 *     VAT rate as the original — a PO line on another rate keeps its own VAT.
 *
 * THE RESULT (applySplit). One line per PO line:
 *   - reconciling an uploaded invoice with its POs: each PO line takes the
 *     invoice's quantity (its share) and price, exactly as a single match does
 *     (billLineReconcile.mergeLines — its PO, PO line, item, account and tax
 *     type kept; its own words, the invoice's beside them).
 *   - otherwise (a bill already on one PO line whose quantity covers others —
 *     Edit Bill on a bill not yet in Xero): copies of the bill line, the same
 *     item, words, account, tax type and price, each on its PO line.
 * Every line is highlighted ("check it") and carries `_split`, with what it
 * was made from, so the split can be undone (unsplitLine) — the invoice's
 * line and the PO lines it took come back as they were, and the line is not
 * split again by itself.
 *
 * AUTOMATIC? As the reconciler's own matches are: a sure split is made as the
 * lines arrive (autoSplit) — exact quantities, one set of PO lines only, and
 * no other invoice line of the same product to share them — and highlighted
 * for the user to check, with Undo. Anything less sure is OFFERED
 * (splitOffers), with a window to choose the PO lines and quantities.
 */

import { mergeLines, isOpenPdfLine, isOpenPoLine, hasPoLine, poLineKey, lineLabel } from './billLineReconcile';
import { billLineQuantityCheck, compareQuantity } from './billQtyCheck';
import { toBillUnits } from './billUnitConversion';
import { effectivePoLine, poQuantityFor } from './billPoEdits';
import { lineWeightKg } from './lineUnits';
import { formatDecimalInput, formatQuantity, formatCount, roundHalfUp } from './numberFormatters';
import { getTaxRate } from './taxTypes';
import { newLineId } from './lineItems';

const EPS = 0.0001;
/** At most this many PO lines of one product are tried in combination. */
export const MAX_CANDIDATES = 12;

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const r4 = (v) => roundHalfUp(v, 4) ?? 0;
// Money exactly as the bill payload rounds it (utils/lineItems.round2 —
// Math.round of the product), in whole pennies so sums never drift.
const pennies = (v) => Math.round((Number(v) || 0) * 100);
const fromPennies = (p) => Math.round(p) / 100;

const codeOf = (v) => String(v || '').trim().toUpperCase();
const words = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** A grid line's VAT rate, as OrderLinesGrid.lineTotals reads it. */
export const rateOf = (line) => (line?.tax_rate != null && line.tax_rate !== '' ? (num(line.tax_rate) || 0) : (getTaxRate(line?.tax_type) || 0));

/** Is this candidate PO line the same product as the bill line? */
export function sameProduct(line, candidate) {
  const code = codeOf(line?.product_code);
  if (code) return codeOf(candidate?.code) === code;
  const w = words(line?.product_name);
  return !!w && words(candidate?.words) === w;
}

/**
 * What a PO line counts for a bill: supplied (else ordered), its cost and
 * unit — the facts the quantity check uses.
 * @param {object} p
 * @param {object} p.po      — the PO (id, purchase_order_number, po_type, line_items)
 * @param {string} p.key     — the PO line's key (billLineReconcile.poLineKey)
 * @param {object|null} [p.ctx]  — its billing context (scope, xeroMade, lines[key].supplied)
 * @param {object|null} [p.edit] — a change typed in its panel (utils/billPoEdits)
 * @param {(code: string) => any} [p.itemWeightOf] — an item code's unit_weight_kg
 * @returns {null|{ poId, key, number, li, ordered, qty, basis, cost, unitWeightKg, itemWeight, code, words }}
 *   null for a line that is not goods (a PO-H, a haulage line) or not found
 */
export function poLineFacts({ po, key, ctx = null, edit = null, itemWeightOf = () => null } = {}) {
  const lines = Array.isArray(po?.line_items) ? po.line_items : [];
  const index = lines.findIndex((li, i) => poLineKey(li, i) === key);
  if (!po || index < 0 || po.po_type === 'haulage' || lines[index]?.kind === 'haulage') return null;
  const li = lines[index];
  const scope = ctx?.scope || null;
  const ordered = poQuantityFor(li, edit, scope);
  const { qty, basis } = compareQuantity({ ordered, supplied: ctx?.lines?.[key]?.supplied, xeroMade: !!ctx?.noSupplied });
  return {
    poId: Number(po.id), key, number: po.purchase_order_number || null, li,
    ordered, qty, basis,
    cost: effectivePoLine(li, edit, scope).unitAmount,
    unitWeightKg: li.unitWeightKg ?? null,
    itemWeight: itemWeightOf ? (itemWeightOf(li.itemCode) ?? null) : null,
    code: li.itemCode || '', words: li.description || '',
  };
}

/**
 * The PO lines still open on the bill while an uploaded invoice is reconciled
 * (billLineReconcile.isOpenPoLine), as split candidates.
 * @param {object[]} lines — the grid's lines
 * @param {(poId, key) => object|null} [factsOf] — poLineFacts for a PO line, where known
 */
export function gridCandidates(lines, factsOf = null) {
  return (Array.isArray(lines) ? lines : [])
    .filter(l => isOpenPoLine(l) && hasPoLine(l) && !l._poHaulageLine && l.po_line_key)
    .map(l => {
      const f = factsOf ? factsOf(l.purchase_order_id, l.po_line_key) : null;
      return {
        id: `${Number(l.purchase_order_id)}|${l.po_line_key}`,
        poId: Number(l.purchase_order_id), key: l.po_line_key,
        number: l.purchase_order_number || f?.number || null,
        gridLineId: l.id,
        code: l.product_code || f?.code || '',
        words: l.product_name || f?.words || '',
        qty: f ? f.qty : (num(l.quantity) ?? 0),
        basis: f?.basis || 'ordered',
        ordered: f ? f.ordered : (num(l.quantity) ?? 0),
        cost: f ? f.cost : num(l.unit_price),
        unitWeightKg: f ? f.unitWeightKg : (l.unit_weight_kg ?? null),
        itemWeight: f?.itemWeight ?? null,
        li: f?.li || null,
      };
    });
}

/**
 * Outside an upload's reconciling (a bill on one PO line whose quantity
 * covers others): the bill's POs' goods lines no other bill line bills, plus
 * the line's own PO line (`own`).
 * @param {object} source — the bill line
 * @param {object[]} lines — the grid's lines
 * @param {object[]} pos — the bill's POs (id, purchase_order_number, po_type, line_items)
 * @param {(poId, key) => object|null} factsOf — poLineFacts for a PO line
 * @param {(line: object) => boolean} [isEmpty]
 */
export function freeCandidates(source, lines, pos, factsOf, isEmpty = () => false) {
  const billed = new Set((Array.isArray(lines) ? lines : [])
    .filter(l => l && l.id !== source?.id && !isEmpty(l) && l.purchase_order_id && l.po_line_key)
    .map(l => `${Number(l.purchase_order_id)}|${l.po_line_key}`));
  const out = [];
  for (const po of pos || []) {
    if (!po || po.po_type === 'haulage') continue;
    (Array.isArray(po.line_items) ? po.line_items : []).forEach((li, i) => {
      const key = poLineKey(li, i);
      const id = `${Number(po.id)}|${key}`;
      if (billed.has(id)) return;
      const f = factsOf(po.id, key);
      if (!f) return;
      const own = Number(source?.purchase_order_id) === Number(po.id) && source?.po_line_key === key;
      out.push({ id, poId: Number(po.id), key, number: f.number, gridLineId: null, own, code: f.code, words: f.words,
        qty: f.qty, basis: f.basis, ordered: f.ordered, cost: f.cost, unitWeightKg: f.unitWeightKg, itemWeight: f.itemWeight, li: f.li });
    });
  }
  return out;
}

const weightOfCandidate = (c) => lineWeightKg(c?.unitWeightKg, c?.itemWeight);
const sumOf = (list, f) => list.reduce((s, x) => s + (Number(f(x)) || 0), 0);

/** The bill line against these PO lines added up (billQtyCheck, in the PO's unit). */
function fitOf(source, parts, item) {
  const poQty = r4(sumOf(parts, p => p.qty));
  const ordered = r4(sumOf(parts, p => p.ordered));
  const costed = parts.filter(p => num(p.cost) > 0);
  const weightOfCost = sumOf(costed, p => p.qty);
  const cost = costed.length
    ? (weightOfCost > EPS ? sumOf(costed, p => p.qty * num(p.cost)) / weightOfCost : num(costed[0].cost))
    : null;
  const first = parts[0];
  const Q = num(source.quantity);
  const u = num(source.unit_price);
  return billLineQuantityCheck({
    item, line: source,
    poLine: { ...(first.li || {}), unitWeightKg: first.unitWeightKg, description: first.words, quantity: poQty },
    itemWeight: first.itemWeight,
    billQty: Q, billCost: u, billAmount: Q !== null && u !== null ? fromPennies(pennies(Q * u)) : null,
    poCost: cost, poQty, ordered,
  });
}

/**
 * Does this bill line cover several PO lines of its product?
 *
 * @param {object} source — the bill line (quantity, unit_price, product_code, product_name, unit_weight_kg)
 * @param {object[]} candidates — gridCandidates / freeCandidates
 * @param {{ item?: object|null }} [opts] — the bill line's inventory item
 * @returns {null|{
 *   sourceId, parts: Array<{ candidate, poQty: number, billQty: number }>,
 *   billQty: number, billInPo: number, poTotal: number, diff: number,
 *   exact: boolean, ambiguous: boolean, level: 'none'|'note', kind, conversion
 * }} parts in the candidates' order; poQty in the PO line's unit, billQty in
 *   the bill line's (adding up to it exactly)
 */
export function findSplit(source, candidates, { item = null } = {}) {
  const Q = num(source?.quantity);
  if (!source || !(Q > EPS)) return null;
  const same = (candidates || []).filter(c => sameProduct(source, c) && num(c.qty) > EPS).slice(0, MAX_CANDIDATES);
  if (same.length < 2) return null;
  // One PO line on its own is a match, not a split.
  if (same.some(c => fitOf(source, [c], item).level !== 'query')) return null;

  const fits = [];
  const n = same.length;
  for (let mask = 1; mask < (1 << n); mask += 1) {
    const parts = same.filter((_, i) => mask & (1 << i));
    if (parts.length < 2) continue;
    const w = weightOfCandidate(parts[0]);
    if (parts.some(p => weightOfCandidate(p) !== w)) continue;
    const check = fitOf(source, parts, item);
    if (check.level === 'query') continue;
    const poTotal = r4(sumOf(parts, p => p.qty));
    fits.push({ parts, check, poTotal, diff: r4(Math.abs((check.billQty ?? 0) - poTotal)) });
  }
  if (!fits.length) return null;
  fits.sort((a, b) => a.diff - b.diff);
  const best = fits[0];
  const ambiguous = fits.length > 1 && fits[1].diff - best.diff < EPS;

  // Each PO line takes what it counts; the difference goes on the largest.
  const billInPo = r4(best.check.billQty);
  const poQtys = best.parts.map(p => r4(p.qty));
  const L = largestIndex(poQtys);
  poQtys[L] = r4(billInPo - sumOf(poQtys.filter((_, i) => i !== L), q => q));
  if (!(poQtys[L] > EPS)) return null;
  const conv = best.check.conversion;
  const billQtys = poQtys.map(q => (conv ? r4(toBillUnits(conv, q)) : q));
  billQtys[L] = r4(Q - sumOf(billQtys.filter((_, i) => i !== L), q => q));
  if (!(billQtys[L] > EPS)) return null;

  return {
    sourceId: source.id,
    parts: best.parts.map((candidate, i) => ({ candidate, poQty: poQtys[i], billQty: billQtys[i] })),
    billQty: r4(Q), billInPo, poTotal: best.poTotal, diff: best.diff,
    exact: best.diff < EPS, ambiguous,
    level: best.check.level, kind: best.check.kind, conversion: conv || null,
  };
}

/** The largest figure's index; the last of equals ("the last/largest line"). */
function largestIndex(list) {
  let at = 0;
  list.forEach((v, i) => { if (Number(v) >= Number(list[at])) at = i; });
  return at;
}

/** A 4 dp price nearest `near` whose q × price comes to `target` pennies, or null. */
function priceFor(q, targetPennies, near) {
  if (!(q > EPS)) return null;
  const base = r4(fromPennies(targetPennies) / q);
  const tries = [];
  for (let k = 0; k <= 50; k += 1) { tries.push(r4(base + k * EPS)); if (k) tries.push(r4(base - k * EPS)); }
  const ok = tries.filter(p => p > 0 && pennies(q * p) === targetPennies);
  if (!ok.length) return null;
  ok.sort((a, b) => Math.abs(a - near) - Math.abs(b - near));
  return ok[0];
}

/**
 * The money of one bill line split by quantity.
 *
 * @param {object} p
 * @param {number|string} p.quantity  — the line's quantity
 * @param {number|string} p.unitPrice — its unit price
 * @param {number} p.rate             — its VAT rate (0.2)
 * @param {number[]} [p.partRates]    — each new line's VAT rate (default: the line's)
 * @param {Array<number|string>} quantities — the new lines' quantities (adding up to the line's)
 * @returns {{
 *   parts: Array<{ quantity, unitPrice, net, tax, priceAdjusted, vatAdjusted }>,
 *   net: number, tax: number, exact: boolean, largest: number
 * }} net / tax — the original line's; exact — the parts add up to both
 */
export function splitAmounts({ quantity, unitPrice, rate = 0, partRates = null }, quantities) {
  const Q = r4(num(quantity) ?? 0);
  const u = r4(num(unitPrice) ?? 0);
  const qs = (quantities || []).map(q => r4(num(q) ?? 0));
  const rates = qs.map((_, i) => (partRates && partRates[i] != null ? Number(partRates[i]) || 0 : Number(rate) || 0));
  const N = pennies(Q * u);
  const T = pennies(Q * u * (Number(rate) || 0));
  const L = largestIndex(qs);
  const prices = qs.map(() => u);
  const nets = qs.map(q => pennies(q * u));
  let priceAdjusted = false;
  const restNet = N - nets.reduce((s, n, i) => (i === L ? s : s + n), 0);
  if (qs.length && nets[L] !== restNet) {
    const p = priceFor(qs[L], restNet, u);
    if (p !== null) { prices[L] = p; nets[L] = restNet; priceAdjusted = true; }
  }
  const netExact = nets.reduce((s, n) => s + n, 0) === N;
  // VAT as the payload works it out (the line's amount × rate, to the penny).
  const taxes = qs.map((q, i) => pennies(q * prices[i] * rates[i]));
  let vatAdjusted = false;
  const sameRate = rates.every(r => Math.abs(r - (Number(rate) || 0)) < 1e-9);
  if (qs.length && sameRate && netExact) {
    const restTax = T - taxes.reduce((s, t, i) => (i === L ? s : s + t), 0);
    if (taxes[L] !== restTax) { taxes[L] = restTax; vatAdjusted = true; }
  }
  const taxExact = !sameRate || taxes.reduce((s, t) => s + t, 0) === T;
  return {
    parts: qs.map((q, i) => ({
      quantity: q, unitPrice: prices[i], net: fromPennies(nets[i]), tax: fromPennies(taxes[i]),
      priceAdjusted: i === L && priceAdjusted, vatAdjusted: i === L && vatAdjusted,
    })),
    net: fromPennies(N), tax: fromPennies(T), exact: netExact && taxExact, largest: L,
  };
}

const withoutSplit = (l) => {
  if (!l) return l;
  const { _split, ...rest } = l;
  return rest;
};

// Tonnes with their capital T; packs and units a count (billQtyCheck's way).
export const qtyText = (q, counted) => (counted ? formatCount(q) : `${formatQuantity(q)} T`);

/** A PO line's own quantity in its own unit: "9.00 T", "40" (packs), "1.50" (unknown). */
export function candidateQtyText(c) {
  const kg = weightOfCandidate(c);
  if (kg === 1000) return `${formatQuantity(c?.qty)} T`;
  if (kg !== null && kg !== undefined) return formatCount(c?.qty);
  return formatQuantity(c?.qty);
}

/**
 * One bill line made into one line per PO line.
 *
 * @param {object[]} lines — the grid's lines
 * @param {{ sourceId: string, parts: Array<{ candidate: object, billQty: number|string }> }} split
 * @returns {{ lines: object[], group: string|null, error: string|null }}
 */
export function applySplit(lines, split) {
  const list = Array.isArray(lines) ? lines : [];
  const source = list.find(l => l.id === split?.sourceId);
  const parts = (split?.parts || []).filter(p => p && p.candidate && num(p.billQty) > EPS);
  if (!source) return { lines: list, group: null, error: 'The line is no longer on the bill.' };
  if (parts.length < 2) return { lines: list, group: null, error: 'Choose at least two PO lines.' };
  const Q = r4(num(source.quantity) ?? 0);
  const total = r4(sumOf(parts, p => num(p.billQty)));
  if (Math.abs(total - Q) > EPS) {
    return { lines: list, group: null, error: `The quantities come to ${formatQuantity(total)}; the line is ${formatQuantity(Q)}.` };
  }
  const grids = parts.map(p => (p.candidate.gridLineId ? list.find(l => l.id === p.candidate.gridLineId) : null));
  if (parts.some((p, i) => p.candidate.gridLineId && !grids[i])) {
    return { lines: list, group: null, error: 'A PO line is no longer open on the bill.' };
  }

  const group = newLineId('split');
  const original = withoutSplit(source);
  // The saved line's own row (bill_line_id) stays with one of its parts:
  // its own PO line's, else the first copy.
  const ownAt = parts.findIndex(p => p.candidate.own);
  const keepIdAt = ownAt >= 0 ? ownAt : parts.findIndex(p => !p.candidate.gridLineId);
  const bases = parts.map((p, i) => {
    if (grids[i]) return mergeLines(grids[i], source);
    const c = p.candidate;
    return {
      ...source,
      id: i === keepIdAt ? source.id : newLineId('split'),
      purchase_order_id: c.poId,
      po_line_key: c.key,
      purchase_order_number: c.number || null,
      _poHaulageLine: false,
      _noPo: false,
    };
  });
  const money = splitAmounts({
    quantity: source.quantity, unitPrice: source.unit_price, rate: rateOf(source),
    partRates: bases.map(rateOf),
  }, parts.map(p => p.billQty));
  const label = lineLabel(original);
  const made = bases.map((b, i) => {
    const m = money.parts[i];
    const notes = [];
    if (m.priceAdjusted) notes.push(`price £${formatDecimalInput(m.unitPrice)} so the lines add up to the invoice line to the penny`);
    if (m.vatAdjusted) notes.push(`VAT £${m.tax.toFixed(2)} so the VAT adds up to the invoice line's`);
    const line = {
      ...withoutSplit(b),
      quantity: formatDecimalInput(m.quantity),
      unit_price: formatDecimalInput(m.unitPrice),
      _highlight: true,
      _hint: `Split from the invoice's line ${label} — ${i + 1} of ${parts.length}; check it${notes.length ? ` (${notes.join('; ')})` : ''}`,
      _split: {
        group, index: i, of: parts.length, original,
        replaced: grids[i] ? withoutSplit(grids[i]) : null,
        priceAdjusted: m.priceAdjusted, vatAdjusted: m.vatAdjusted,
      },
    };
    if (m.vatAdjusted) line._vat = { net: m.net, tax: m.tax, tax_type: b.tax_type || null };
    else delete line._vat;
    return line;
  });

  // In place: a PO line taken stays where it was; copies go where the line was.
  const byGrid = new Map(grids.map((g, i) => [g?.id, made[i]]).filter(([id]) => id));
  const copies = made.filter((_, i) => !grids[i]);
  const out = [];
  for (const l of list) {
    if (l.id === source.id) { out.push(...copies); continue; }
    if (byGrid.has(l.id)) { out.push(byGrid.get(l.id)); continue; }
    out.push(l);
  }
  return { lines: out, group, error: null };
}

/**
 * Undo a split: the line it was made from back in the first part's place, the
 * PO lines it took back as they were; the line is not split again by itself.
 */
export function unsplitLine(lines, group) {
  const list = Array.isArray(lines) ? lines : [];
  const parts = list.filter(l => l?._split?.group === group);
  if (!parts.length) return list;
  const original = { ...parts[0]._split.original, _noAutoSplit: true };
  const out = [];
  let placed = false;
  for (const l of list) {
    if (l?._split?.group !== group) { out.push(l); continue; }
    if (!placed) { out.push(original); placed = true; }
    if (l._split.replaced) out.push(l._split.replaced);
  }
  return out;
}

/** The splits on the bill, for their Undo: [{ group, original, lines }]. */
export function appliedSplits(lines) {
  const groups = new Map();
  for (const l of Array.isArray(lines) ? lines : []) {
    const s = l?._split;
    if (!s) continue;
    if (!groups.has(s.group)) groups.set(s.group, { group: s.group, original: s.original, lines: [] });
    groups.get(s.group).lines.push(l);
  }
  return [...groups.values()];
}

/**
 * The splits to offer while an uploaded invoice is reconciled: each invoice
 * line on no PO line, against the PO lines still open.
 * @param {object[]} lines
 * @param {{ factsOf?: Function, itemOf?: (line) => object|null }} [opts]
 * @returns {Array<object>} findSplit results
 */
export function splitOffers(lines, { factsOf = null, itemOf = () => null } = {}) {
  const list = Array.isArray(lines) ? lines : [];
  const cands = gridCandidates(list, factsOf);
  if (cands.length < 2) return [];
  return list
    .filter(l => isOpenPdfLine(l) && !l._noPo)
    .map(l => findSplit(l, cands, { item: itemOf(l) }))
    .filter(Boolean);
}

/**
 * The sure splits, made as the lines arrive (as autoMatch makes sure matches):
 * an exact one, one set of PO lines only, no other invoice line of the same
 * product to share them, and not one the user undid.
 * @returns {{ lines: object[], split: number }}
 */
export function autoSplit(lines, { factsOf = null, itemOf = () => null } = {}) {
  let out = Array.isArray(lines) ? lines.slice() : [];
  let split = 0;
  for (const pdf of out.filter(l => isOpenPdfLine(l) && !l._noPo && !l._noAutoSplit)) {
    const rivals = out.some(o => o.id !== pdf.id && isOpenPdfLine(o) && !o._noPo
      && sameProduct(pdf, { code: o.product_code, words: o.product_name }));
    if (rivals) continue;
    const offer = findSplit(pdf, gridCandidates(out, factsOf), { item: itemOf(pdf) });
    if (!offer || !offer.exact || offer.ambiguous) continue;
    const res = applySplit(out, offer);
    if (res.error) continue;
    out = res.lines;
    split += 1;
  }
  return { lines: out, split };
}

/**
 * An offer, in a sentence: "Opti AB · 1,000.00 @ £40.5 covers PO-20406A
 * (100.00) and PO-20406 (900.00) — split it across them."
 */
export function offerText(offer, source) {
  if (!offer) return '';
  const counted = offer.kind === 'bags' || offer.kind === 'units';
  const bits = offer.parts.map(p => `${p.candidate.number || 'a PO'} (${qtyText(p.candidate.qty, counted)}${p.candidate.basis === 'supplied' ? ' supplied' : ''})`);
  const list = bits.length > 1 ? `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}` : bits[0];
  let text = `${lineLabel(source)} covers ${list} — split it across them.`;
  if (!offer.exact) {
    const largest = offer.parts[largestIndex(offer.parts.map(p => p.poQty))];
    text += ` They come to ${qtyText(offer.poTotal, counted)}; the difference goes on ${largest?.candidate?.number || 'the largest'}.`;
  }
  if (offer.ambiguous) text += ' Other PO lines fit as well — choose which.';
  return text;
}
